import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type HotspotShapeType, type PlaybookType, type QuickGuiddeType } from 'app/types'
import { isQG } from 'modules'

// Minimal typing for HTMLVideoElement.requestVideoFrameCallback — frame-accurate `mediaTime`,
// sampled at the video's fps (~25–60/s) instead of timeupdate's coarse ~4/s.
type FrameMetadata = { mediaTime: number }

// Fire the entry gate this many seconds before the step boundary (~2 frames) so the pause lands on
// stepStart without the new step's audio/first frame leaking during the reactive pause latency.
const GATE_LEAD = 0.06

type HotspotStep = {
    index: number
    stepId: string
    stepStart: number
    stepEnd: number
    hotspots: Array<HotspotShapeType>
}

// Earliest not-yet-resolved hotspot step whose gate point (stepStart minus a small `lead`) falls in
// (prevTime, currTime]. A "crossing" test (vs a fixed window) still catches the boundary after an
// overshoot instead of skipping it. The `lead` fires the gate a hair BEFORE the step boundary so the
// pause lands on stepStart without the new step's audio leaking (the caller seeks to exact stepStart).
export const findCrossedGate = (
    timeline: Array<HotspotStep>,
    resolved: Set<number>,
    prevTime: number,
    currTime: number,
    lead = 0,
): HotspotStep | null =>
    timeline.find(
        step =>
            step.hotspots.length > 0 &&
            !resolved.has(step.index) &&
            step.stepStart - lead > prevTime &&
            step.stepStart - lead <= currTime,
    ) ?? null

export type HotspotPlayback = {
    hotspots: Array<HotspotShapeType>
    isGated: boolean
    runAction: (_hotspot: HotspotShapeType) => void
}

/**
 * Viewer playback controller for hotspots (embed). Drives the raw `<video>` element directly. The
 * merged video plays linearly; a hotspot is an ENTRY gate — when playback reaches the START of a step
 * that has hotspots, the video pauses and shows them, and only a click continues. Hotspots aren't
 * shown while a step plays, and seeking into the middle of a hotspot step bypasses the gate.
 */
export const useHotspotPlayback = (
    playbook: PlaybookType | QuickGuiddeType,
    video: HTMLVideoElement | null,
    enabled = true,
): HotspotPlayback => {
    const timeline = useMemo<Array<HotspotStep>>(() => {
        if (!isQG(playbook) || !playbook.steps.length) return []

        let elapsed = 0
        return playbook.steps.map((step, index) => {
            const stepStart = elapsed
            const stepEnd = stepStart + (step.duration ?? 0) + (step.transition?.duration || 0)
            elapsed = stepEnd

            return {
                index,
                stepId: step.id,
                stepStart,
                stepEnd,
                hotspots: (step.layers ?? []).filter(layer => layer.type === 'hotspot' && Boolean(layer.enabled)),
            }
        })
    }, [playbook])

    const [gatedIndex, setGatedIndex] = useState<number | null>(null)

    // Refs so the single time-update listener always reads fresh state
    const gatedRef = useRef<number | null>(null)
    gatedRef.current = gatedIndex
    const resolvedRef = useRef<Set<number>>(new Set())
    const lastTimeRef = useRef(0)

    const gateStep = useCallback(
        (index: number) => {
            gatedRef.current = index
            setGatedIndex(index)
            if (!video) return
            video.pause()
            video.currentTime = Math.max(0, timeline[index]?.stepStart ?? 0) // freeze at the step entry
        },
        [video, timeline],
    )

    useEffect(() => {
        if (!enabled || !video || !timeline.length) return
        const el = video

        const frameApi = el as unknown as {
            requestVideoFrameCallback?: (_cb: (_now: number, _metadata: FrameMetadata) => void) => number
            cancelVideoFrameCallback?: (_handle: number) => void
        }
        const hasRvfc = typeof frameApi.requestVideoFrameCallback === 'function'

        let handle = 0
        let seeking = false

        function sample(_now: number, metadata: FrameMetadata) {
            if (!seeking) {
                const time = metadata.mediaTime
                const prev = lastTimeRef.current
                lastTimeRef.current = time

                if (gatedRef.current === null && time >= prev) {
                    const crossed = findCrossedGate(timeline, resolvedRef.current, prev, time, GATE_LEAD)
                    if (crossed) gateStep(crossed.index)
                }
            }

            handle = schedule()
        }

        function schedule(): number {
            if (hasRvfc) return frameApi.requestVideoFrameCallback!(sample)
            return requestAnimationFrame(now => sample(now, { mediaTime: el.currentTime }))
        }

        const cancel = () => {
            if (hasRvfc) frameApi.cancelVideoFrameCallback?.(handle)
            else cancelAnimationFrame(handle)
        }

        const onSeeking = () => {
            seeking = true
        }
        const onSeeked = () => {
            seeking = false
            const time = el.currentTime
            if (gatedRef.current === null && time < lastTimeRef.current) {
                resolvedRef.current.forEach(index => {
                    if ((timeline[index]?.stepStart ?? 0) >= time) resolvedRef.current.delete(index)
                })
            }
            lastTimeRef.current = time
        }

        handle = schedule()
        video.addEventListener('seeking', onSeeking)
        video.addEventListener('seeked', onSeeked)

        return () => {
            cancel()
            video.removeEventListener('seeking', onSeeking)
            video.removeEventListener('seeked', onSeeked)
        }
    }, [enabled, video, timeline, gateStep])

    // If the first step has a hotspot, gate it immediately (before any play), so the viewer sees the
    // hotspot straight away with no play button — a click then starts playback.
    useEffect(() => {
        if (!enabled || !video || !timeline.length || gatedRef.current !== null) return

        const firstStep = timeline[0]
        if (firstStep?.stepStart === 0 && firstStep.hotspots.length && !resolvedRef.current.has(firstStep.index)) {
            gateStep(firstStep.index)
        }
    }, [enabled, video, timeline, gateStep])

    const runAction = useCallback(
        (hotspot: HotspotShapeType) => {
            const resolveGate = () => {
                if (gatedRef.current !== null) resolvedRef.current.add(gatedRef.current)
                gatedRef.current = null
                setGatedIndex(null)
            }

            if (hotspot.action === 'goToStep') {
                const target = timeline.find(step => step.stepId === hotspot.actionTargetStepId)
                resolveGate()
                if (!target || !video) return

                video.currentTime = target.stepStart
                lastTimeRef.current = target.stepStart

                if (target.hotspots.length && !resolvedRef.current.has(target.index)) gateStep(target.index)
                else video.play().catch(() => {})
                return
            }

            resolveGate()
            video?.play().catch(() => {})
        },
        [video, timeline, gateStep],
    )

    const gatedStep = enabled && gatedIndex !== null ? timeline[gatedIndex] : null

    return {
        hotspots: gatedStep?.hotspots ?? [],
        isGated: Boolean(gatedStep),
        runAction,
    }
}
